/**
 * Client details overview — dedicated store for upcoming tasks & appointments
 * so Timeline (TaskState / LeadWidget) stays independent.
 */
import {createSlice} from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import {endPoints} from "../../api/endpoints";
import {formatAxiosErrorMessage, qargsToQstring} from "../../utils/common";

const emptyList = () => ({
    isLoading: false,
    data: [],
    error: null,
});

const ClientDetailsInitialState = {
    upcomingTasks: emptyList(),
    upcomingAppointments: emptyList(),
};

const unwrapList = (payload) => {
    const body = payload?.data ?? payload ?? null;
    if (Array.isArray(body)) return body;
    if (Array.isArray(body?.results)) return body.results;
    if (Array.isArray(body?.appointments)) return body.appointments;
    return [];
};

export const ClientDetailsSlice = createSlice({
    name: "clientDetails",
    initialState: {...ClientDetailsInitialState},
    reducers: {
        upcomingTasksRequesting: (state) => {
            state.upcomingTasks.isLoading = true;
            state.upcomingTasks.error = null;
        },
        upcomingTasksSuccess: (state, action) => {
            state.upcomingTasks.isLoading = false;
            state.upcomingTasks.data = action.payload || [];
            state.upcomingTasks.error = null;
        },
        upcomingTasksError: (state, action) => {
            state.upcomingTasks.isLoading = false;
            state.upcomingTasks.error = action.payload;
        },
        upcomingTasksReset: (state) => {
            state.upcomingTasks = emptyList();
        },

        upcomingAppointmentsRequesting: (state) => {
            state.upcomingAppointments.isLoading = true;
            state.upcomingAppointments.error = null;
        },
        upcomingAppointmentsSuccess: (state, action) => {
            state.upcomingAppointments.isLoading = false;
            state.upcomingAppointments.data = action.payload || [];
            state.upcomingAppointments.error = null;
        },
        upcomingAppointmentsError: (state, action) => {
            state.upcomingAppointments.isLoading = false;
            state.upcomingAppointments.error = action.payload;
        },
        upcomingAppointmentsReset: (state) => {
            state.upcomingAppointments = emptyList();
        },

        clientDetailsMasterReset: () => ClientDetailsInitialState,
    },
});

export const {
    upcomingTasksRequesting,
    upcomingTasksSuccess,
    upcomingTasksError,
    upcomingTasksReset,
    upcomingAppointmentsRequesting,
    upcomingAppointmentsSuccess,
    upcomingAppointmentsError,
    upcomingAppointmentsReset,
    clientDetailsMasterReset,
} = ClientDetailsSlice.actions;

/**
 * GET /task/?client=&status=pending&due_or_null_after=&limit=
 * Pending upcoming tasks for the overview card.
 */
export const getUpcomingTasksAPI =
    ({clientId, limit = 5} = {}) =>
    async (dispatch) => {
        if (!clientId) return;
        dispatch(upcomingTasksRequesting());
        try {
            const dueAfter = new Date().toISOString().split("T")[0];
            const query = qargsToQstring({
                client: clientId,
                status: "pending",
                due_or_null_after: dueAfter,
                due__isnull: false,
                ordering: "due",
                limit,
                offset: 0,
            });
            const response = await apiService.get(
                `${endPoints.task.list}${query}`,
                {}
            );
            const rows = unwrapList(response.data);
            dispatch(upcomingTasksSuccess(rows.slice(0, limit)));
        } catch (error) {
            dispatch(upcomingTasksError(formatAxiosErrorMessage(error)));
        }
    };

/**
 * Upcoming appointments via normal calendar list:
 * GET /appointments/?client=&start_time_after=
 */
export const getUpcomingAppointmentsAPI =
    ({clientId, limit = 5} = {}) =>
    async (dispatch) => {
        if (!clientId) return;
        dispatch(upcomingAppointmentsRequesting());
        try {
            const startAfter = new Date().toISOString().split("T")[0];
            const query = qargsToQstring({
                client: clientId,
                start_time_after: startAfter,
                ordering: "start_time",
                limit,
                offset: 0,
            });
            const response = await apiService.get(
                `${endPoints.lead.calendarAppointment}${query}`,
                {}
            );
            const rows = unwrapList(response.data).filter(
                (a) => a && a.cancelled !== true
            );
            dispatch(upcomingAppointmentsSuccess(rows.slice(0, limit)));
        } catch (error) {
            dispatch(upcomingAppointmentsError(formatAxiosErrorMessage(error)));
        }
    };

export const ClientDetailsState = (state) => state.clientDetails;
export default ClientDetailsSlice.reducer;
